import React, { SyntheticEvent, useCallback } from 'react';
import { Typography } from '@biome-design-system/ui/Typography';
import { IconButton } from '@biome-design-system/ui/IconButton';
import { CloseIcon } from '@biome-design-system/icons/CloseIcon';
import { injectIntl, WrappedComponentProps } from '@kyruus/intl';
import { PMCConfig } from '@kyruus/types';
import {
  Log,
  ExtendedProvider,
  FullAppointmentInfo,
  SearchSummary
} from 'Src/types';
import { AppDrawer, APP_DRAWER_IDS } from 'Src/app-drawer';
import { CtaList } from './CtaList';
import { Header } from './styles';
import { messages } from './lib';
import { getProviderDisplayName } from 'Src/provider/utils';
interface BaseCtaDrawerProps {
  id: string;
  log: Log;
  onClose: (e: SyntheticEvent) => void;
  provider: ExtendedProvider;
  config: PMCConfig;
  searchSummary?: SearchSummary;
  apptOptions?: FullAppointmentInfo;
}
type CtaDrawerProps = BaseCtaDrawerProps & WrappedComponentProps;

export const CtaDrawer = injectIntl(
  ({
    onClose,
    log,
    provider,
    config,
    searchSummary,
    apptOptions,
    intl
  }: CtaDrawerProps) => {
    const closeButtonRef = useCallback((closeButtonNode) => {
      if (closeButtonNode) {
        closeButtonNode.focus();
      }
    }, []);

    const handleClose = (
      event: SyntheticEvent,
      reason?: 'closeIcon' | 'escapeKeyDown' | 'backdropClick'
    ) => {
      log(`user_action.alter_view.request_appointment.${reason}`, {
        providerId: provider?.id
      });
      onClose(event);
    };
    const headingId = `${APP_DRAWER_IDS.CTA_DRAWER}-title`;
    const providerName = getProviderDisplayName(provider);
    return (
      <AppDrawer
        id={APP_DRAWER_IDS.CTA_DRAWER}
        onClose={handleClose}
        aria-labelledby={headingId}
      >
        <Header>
          <Typography id={headingId} variant="h5" component="h2">
            {intl.formatMessage(messages.ctaDrawerHeading, {
              providerName
            })}
          </Typography>
          <IconButton
            aria-controls={APP_DRAWER_IDS.CTA_DRAWER}
            aria-label={intl.formatMessage(messages.closeCtaDrawerAriaLabel)}
            color="primary"
            data-testid="close-drawer-button"
            variant="text"
            onClick={(e: React.SyntheticEvent) => handleClose(e, 'closeIcon')}
            ref={closeButtonRef}
          >
            <CloseIcon />
          </IconButton>
        </Header>
        <CtaList
          provider={provider}
          config={config}
          searchSummary={searchSummary}
          apptOptions={apptOptions}
          onClose={handleClose}
          log={log}
        />
      </AppDrawer>
    );
  }
);
